// Store
import Decimal from 'decimal.js';

export interface RootState {
  devices: DeviceState;
  gateways: GatewayState;
  login: LoginState;
  loading: LoadingState;
  organisations: OrganisationState;
  versions: DeviceVersionState;
  batches: DeviceBatchState;
  projects: ProjectState;
  sites: SiteState;
  locations: LocationState;
  users: UserState;
  notifications: NotificationState;
  sidebar: SidebarState;
  roles: RoleState;
  reporting: ReportingState;
  widget: WidgetState;
  rules: RulesState;
  dataSets: DataSetState;
  locationsV2: LocationsV2State;
  jobs: JobState;
  explorer: DataExplorerState;
  datatable: DataTableState;
  subscriptions: SubscriptionsState;
  accounts: AccountState;
}

export interface DeviceState {
  initialState: () => DeviceState;
  devices: Device[];
  filters: any;
  total: number;
  isNavigation: boolean;
}

export interface GatewayState {
  initialState: () => GatewayState;
  gateways: Gateway[];
  useGatewaySubscriptions: boolean;
}

export interface AdminDeviceTypeState {
  initialState: () => AdminDeviceTypeState;
  deviceTypes: DeviceType[];
  filters: any;
  total: number;
}

export interface DeviceBatchState {
  initialState: () => DeviceBatchState;
  activeBatch: DeviceBatch;
}

export interface DeviceVersionState {
  initialState: () => DeviceVersionState;
  versions: DeviceVersion[];
  total: number;
}

export interface ProjectState {
  initialState: () => ProjectState;
  projects: Project[];
  total: number;
  activeProjectId?: number | null;
  filters: any;
}

export interface TaskState {
  initialState: () => TaskState;
  tasks: Task[];
  total: number;
  filters: any;
}

export interface SiteState {
  initialState: () => SiteState;
  sites: Site[];
  filters: any;
  total: number;
}

export interface ApplicationState {
  initialState: () => ApplicationState;
  applications: Application[];
  filters: any;
  total: number;
}

export interface AdminApplicationState {
  initialState: () => AdminApplicationState;
  applications: Application[];
  filters: any;
  total: number;
  availableDashboards: [];
}

export interface UserState {
  initialState: () => UserState;
  users: User[];
  total: number;
  filters: any;
}

export interface RoleState {
  initialState: () => RoleState;
  roles: Role[];
}

export interface LocationState {
  initialState: () => LocationState;
  locations: Location[];
  total: number;
}

export interface LoadingState {
  initialState: () => LoadingState;
  loading: boolean;
  bypass: boolean;
  overlay: boolean;
  requestCount: number;
  showInterface: boolean;
}

export interface LoginState {
  initialState: () => LoginState;
  token: string | null;
  details: object;
  permissions: string[];
  originalToken: string | null;
  lastOrganisation: number | null;
  experimental: boolean;
  showAllOrganisations: boolean;
}

export interface AccountState {
  initialState: () => AccountState;
  accounts: Account[];
  activeAccountId?: number | null;
}

export interface OrganisationState {
  initialState: () => OrganisationState;
  organisations: Organisation[];
  activeOrganisationId?: number | null;
  hasGatewaySettings?: boolean;
}

export interface NotificationState {
  initialState: () => NotificationState;
  toastErrorMessages: { [id: string]: string };
  notificationBanner: NotificationBanner | null;
  dismissedBanners: number[];
}

export interface SidebarState {
  initialState: () => SidebarState;
  isSidebarCollapsed: boolean;
}

export interface ReportingState {
  initialState: () => ReportingState;
  organisationCounts: OrganisationCounts;
  projectCounts: ProjectCounts;
}

export interface WidgetState {
  initialState: () => WidgetState;
  widgetFilters: string[];
  widgets: Widget[];
}
export interface RulesState {
  initialState: () => RulesState;
  rules: DeviceVersionRule[];
  ruleConditions: RuleConditions[];
  uplinkSchemas: SchemaField[];
}
export interface DataSetState {
  initialState: () => DataSetState;
  dataSets: DataSet[];
}
export interface LocationsV2State {
  initialState: () => LocationsV2State;
  locationFilters: string[];
  showPositions: boolean;
  showAddPositionDialog: boolean;
}
export interface JobState {
  initialState: () => JobState;
  activeJobs: Job[];
  completedJobs: Job[];
}
export interface DataExplorerState {
  initialState: () => DataExplorerState;
  lastView: { id: number, organisation_id: number } | null;
}

export interface DataTableState {
  initialState: () => DataTableState;
  paginationOptions: {[key: string]: PaginationOptions};
}

export interface SubscriptionsState {
  initialState: () => SubscriptionsState;
  layout: { headers: Array<{[key: string]: string | boolean}>, removedHeaders: Array<{[key: string]: string | boolean}> };
}

// Models
export interface OrganisationCounts {
  projects: number;
  tasks: number;
  catalogue: number;
  devices: number;
  sites: number;
  users: number;
  gateways: number;
}

export interface ProjectCounts {
  tasks: number;
  devices: number;
  sites: number;
  users: number;
  gateways: number;
}

export interface DataTable {
  activeFilterOptions: object;
  activePage: number;
  entity: string;
  perPageValue: number;
  orderOptions: {
    sort?: string,
    desc?: boolean,
  };
}

export interface IDataTableSelectableFilterValue {
  id: string | number;
  name: string;
}

export interface IDataTableHeader {
  id?: string | number;
  value: string;
  text: string;
  align?: string;
  filterable?: boolean;
  timeFilter?: boolean;
  sortable?: boolean;
  filterItemClass?: string;
  allowedFilterValues?: Array<IDataTableSelectableFilterValue | string | number>;
  queryKey?: string;
  querySortKey?: string;
  timeFilterKey?: string;
  singleSelectAdvancedFilters?: boolean;
  isCustomField?: boolean;
  hideSort?: boolean;
  class?: string;
  cellClass?: string;
  width?: string;
  tooltipText?: string;
  requiredPermissions?: string[];
}

export interface Device {
  id: number;
  device_id: string | null;
  device_version_id: number | null;
  network_id: string | null;
  status_id: number;
  stage_id: number;
  assembly_id?: number;
  location_id: number | null;
  chip_id: number | null;
  commissioned: Date | null;
  project_id: number | null;
  uuid: string | null;
  site_id: number | null;
  last_seen: Date | null;
  created_at: Date | null;
  updated_at: Date | null;
  deleted_at: Date | null;
  chip?: Chip;
  license: License;
  device_version?: DeviceVersion;
  network_device_type?: NetworkDeviceType;
  organisation?: Organisation;
  project?: Project;
  device_deployment?: DeviceDeployment;
  site?: Site;
  status: Status;
  stage: Stage;
  location?: Location;
  subscription?: Subscription;
  position?: Position;
  position_id?: number;
  iccid?: string;
  managed_by_daizy: boolean;
  is_monitored: boolean;
  lns_id: number | null;
  lns_name: string | null;
}

export interface DeviceType {
  id: number;
  manufacturer_id: number;
  name: string;
  bearer: string;
  // device_model: DeviceModel;
  device_model_name: string;
  manufacturer_name: string;
  slug: string;
  manufacturer_reference: string;
  configuration_profiles: ConfigurationProfile[];
  requires_sim: boolean;
}

export interface DeviceModel {
  id: number;
  name: string;
  manufacturer: Manufacturer;
}

export interface Manufacturer {
  id: number;
  name: string;
}

export interface Network {
  id: number;
  name: string;
}

export interface ChipManufacturer {
  id: number;
  name: string;
}

export interface Project {
  id: number;
  organisation_id: string | null;
  name: string | null;
  status: string;
  user_id: number;
  republish_mqtt: boolean;
  created_at: Date | null;
  updated_at: Date | null;
  deleted_at: Date | null;
  user: any | null;
  devices: [];
  devices_count: number;
  devices_active_count: number;
  devices_inactive_count: number;
  devices_fault_count: number;
  sites_count: number;
}

export interface Task {
  id: number;
  organisation_id: number;
  type: string | null;
  project_id: number;
  position_id: number;
  device_id: number;
  completed_by_user_id: number;
  task_note: string;
  due_at: Date | null;
  completed_at: Date | null;
  created_at: Date | null;
  updated_at: Date | null;
  deleted_at: Date | null;
  completed_user: User | null;
  organisation: Organisation | null;
  project: Project | null;
  position: Position | null;
  device: Device | null;
}

export interface DeviceGatewayTask {
  id: number;
  type: string;
  organisation_id: number;
  project_id?: number;
  project?: Partial<Project>;
  position_id: number;
  position: GatewayPosition | Position | null;
  device_gateway_id: number;
  assigned_user_id?: number;
  assigned_user: Partial<User>;
  completed_by_user_id?: number;
  completed_user: Partial<User>;
  due_at?: Date;
  notes: string | null;
  created_at?: Date;
  completed_at?: Date;
  task_source_type: string;
  project_name: string;
  location: Partial<LocationV2>;
  site: Partial<LocationV2>;
  device_gateway_type: { id: number; name: string; original_id: number };
}

export interface DeviceDeployment {
  id: number;
  project_id: number;
  group_id: number;
  user_id: number;
  uuid: string;
  count: number;
  deployed_at: Date | null;
  created_at: Date | null;
  updated_at: Date | null;
  deleted_at: Date | null;
}

export interface Chip {
  id: number;
  chip_manufacturer_id: number;
  assigned: number;
  identifier: string;
  created_at: Date | null;
  updated_at: Date | null;
  deleted_at: Date | null;
  deleted: boolean;
}

export interface DeviceVersion {
  id: number;
  name: string;
  bearer: string;
  device_category?: string;
  device_model_id: number;
  data_sample: string | null;
  data_mapping: string | null;
  sigfox_certificate_id: string | null;
  sigfox_device_type_id: string | null;
  image: string | null;
  chip_manufacturer_id: number | null;
  schema_fields: SchemaField[] | null;
  details: string | null;
  created_at: Date | null;
  updated_at: Date | null;
  deleted_at: Date | null;
  lan_protocol: 'MBus'| 'Modbus'| null;
  interfaces?: Array<{ interface: Interface}>;
  require_local_node_addresses?: boolean;
  marketing_details?: MarketingDetails;
  device_class?: string;
  lorawan_version?: string;
  lorawan_region?: string;
  configuration_profiles: ConfigurationProfile[];
}

export interface GatewayTask {
  id: number;
  type: string;
  organisationId: number;
  projectId?: number;
  gatewayPositionId?: number;
  gatewayId?: number;
  assignedUserId?: number;
  completedByUserId?: number;
  dueAt?: Date;
  createdAt?: Date;
  completedAt?: Date;
}

export interface SchemaField {
  id: number;
  device_version_id: number;
  direction: 'uplink' | 'downlink';
  fieldName: string;
  measurement: string;
  type: 'float' | 'int' | 'string' | 'boolean';
  message_type: string;
  description: string;
}

export interface Location {
  id: number;
  name: string;
  site_id: number;
  full_name: string;
  created_at: Date | null;
  updated_at: Date | null;
  total: number;
}

export interface NetworkDeviceType {
  id: number;
  device_version_id: number;
  network_id: number;
  organisation_id: number;
  network_device_type_id: string | null;
  name: string;
  description: string;
  created_at: Date | null;
  updated_at: Date | null;
  deleted_at: Date | null;
}

export interface Organisation {
  id: number;
  name: string;
  billing_account?: CommercialAccount;
  created_at: string | null;
  update_at: string | null;
  deleted_at: string | null;
  active: boolean;
  subscribed_stock?: number;
  unsubscribed_stock?: number;
  slug?: string;
}


export interface Account {
  id: number;
  name: string;
}

export interface Site {
  id: number;
  project_id: number;
  assigned_to_id?: number;
  name: string;
  type: string;
  lng: number;
  lat: number;
  notes: string | null;
  address: string | null;
  telephone: string | null;
  contact_name: string | null;
  contact_email: string | null;
  contact_telephone: string | null;
  deleted_at: Date | null;
  created_at: Date | null;
  updated_at: Date | null;
  project: Project;
  location?: string[];
  locations: Location[];
  locations_total: number;
  devices: Device[];
  devices_total: number;
}

export interface Position {
  id: number;
  project_id: number;
  site_id: number;
  location_id: number;
  device_version_id: number;
  device_id: number;
  device?: Device;
  status_id: number;
  notes: string;
  photo_guid: string;
  site: Site | null;
  device_version?: DeviceVersion | null;
  location: Location | null;
  configuration_profile_id: number;
  configuration_profiles: ConfigurationProfile[];
  assembly_id?: number;
  assembly?: any;
  vend_coordinates?: boolean;
}

export interface PositionNote {
  id: number;
  positionId: number;
  body: string;
  authorId: number | null;
  authorName: string | null;
  createdAt: string;
  updatedAt: string | null;
  edited: boolean;
}

export interface PositionNotesPage {
  notes: PositionNote[];
  nextOffset: number | null;
}

export interface PositionPhoto {
  id: number;
  sort_order: number;
  url: string;
}

export interface PositionPhotoList {
  photos: PositionPhoto[];
  total: number;
  maxPhotos: number;
}

export interface PositionPhotoUploadUrl {
  photoId: number;
  fileName: string;
  urlInfo: UploadImageUrlResponse['urlInfo'];
}

export interface User {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  username: string;
  is_api_user: boolean;
  created_at: Date | null;
  updated_at: Date | null;
  deleted_at: Date | null;
  projects: object[];
  roles: object[];
  mfa_enabled?: boolean;
  last_login?: Date | null;
}

export interface Role {
  id: number;
  name: string;
}

export interface Stage {
  id: number;
  name: string;
  created_at: Date | null;
  updated_at: Date | null;
}

export enum StageType {
  UNKNOWN = 1,
  DEPLOYED = 2,
  READY_FOR_INSTALL = 3,
  STOCK = 4,
  COMMISSIONED = 5,
  TEST = 6,
}

export interface Status {
  id: number;
  name: string;
  created_at: Date | null;
  updated_at: Date | null;
}

export enum StatusType {
  UNKNOWN = 1,
  ACTIVE = 2,
  INACTIVE = 3,
  FAULT = 4,
  LOST = 5,
}

export interface DeviceBatchRow {
  device_id: string;
  device_pac: string;
  custom_id: string;
  app_key: string;
}

export interface DeviceBatch {
  id: string;
  device_version_id: number;
  device_ids: DeviceBatchRow[];
  project_id: number | null;
  group_id: number | null;
  group_name: string;
  bearer: string;
  managedByDaizy: boolean;
}

export interface Application {
  id: string;
  name: string;
  type: string;
  src: string;
  organisation_id: number;
  reference: string;
  organisations: Organisation[];
}

export interface App {
  id: string;
  name: string;
  publisher: string;
  description: string;
  versions: AppVersion[];
}

export interface AppVersion {
  id: number;
  application_id: string;
  version: string;
}

export interface AppAssociation {
  organisation_id: number;
  application_id: string;
  project_id: number;
  application: App;
}


export interface Role {
  id: number;
  name: string;
  slug: string;
  context: string;
  admin: boolean;
}

export interface ConfigurationProfile {
  id: number;
  device_version_id: number;
  name: string;
  organisation_id: number;
  global_default: boolean;
  organisation_default: boolean;
  configuration_profile_messages: Array<{ id: number; message_type: string; configuration_profile_values: any; configuration_profile_id: number; }>;
}

export interface Widget {
  id: string;
  title: string;
  payload: any;
  source: string;
  dataEndpoint: string;
  x: number;
  y: number;
  width: number;
  height: number;
  enabledFields: string[];
}

export interface DeviceVersionRule {
  id?: number | undefined;
  name: string;
  desc: string | null;
  device_version_id: number;
  conditions: RuleConditions | null;
  message: string;
  severity: string;
  created_at?: Date | null | undefined;
  updated_at?: Date | null | undefined;
  deleted_at?: Date | null | undefined;
  message_type: string;
}

export interface RuleConditions {
  any?: Array<JSONRule | RuleConditions> | undefined;
  all?: Array<JSONRule | RuleConditions> | undefined;
}

export interface JSONRule {
  fact: string;
  path: string;
  operation: string;
  value: any;
}

export interface EmailChannelDetails {
  address: string;
}

export interface SlackChannelDetails {
  url: string;
}

export interface OpsGenieChannelDetails {
  url: string;
  authKey: string;
}

export interface AWSIoTChannelDetails {
  token: string;
}

export interface WebhookChannelDetails {
  headers: {[key: string]: string};
  url: string;
}

export interface MQTTChannelDetails {
  endpoint: string;
  username?: string;
  password?: string;
  clientId?: string;
  topic: string;
  qos: number;
}

export interface WebsocketChannelDetails {
  endpoint: string;
  token: string;
}

export interface S3BucketChannelDetails {
  region: string;
  bucketName: string;
  accessKeyId?: string;
  secretAccessKey?: string;
  roleARN?: string;
  externalId?: string;
}

export interface QSS3ChannelDetails {
  region: string;
  bucketName: string;
  accessKeyId?: string;
  secretAccessKey?: string;
  roleARN?: string;
  externalId?: string;
}

export interface SFTPChannelDetails {
  host: string;
  username: string;
  password?: string;
  privateKey?: string;
  port: number;
}

export interface AzureDataLakeChannelDetails {
  accountName: string;
  accountKey?: string;
  token?: string;
  containerName?: string;
}

export interface MySQLChannelDetails {
  host: string;
  port: number;
  database: string;
  username: string;
  password: string;
}

export interface IntegrationConfiguration {
  id?: number | undefined;
  type?: string;
  entityId: number;
  integrationId: number;
  integration: Integration | DeviceIntegration;
  severity: number;
  suppress_repeats: boolean;
  projectId?: number;
}
export interface StreamingIntegrationConfiguration {
  id?: number | undefined;
  integration_id: number;
  integration?: Integration;
  alerts_severity?: number;
  alerts_suppress_repeats?: boolean;
  alerts?: boolean;
  alerts_cleared?: boolean;
  dataset_updates?: boolean;
  include_location_references?: boolean;
  include_location_attributes?: boolean;
  include_site_references?: boolean;
  include_site_attributes?: boolean;
  had_info_severity: boolean;
}

export interface PublishingIntegrationConfiguration {
  id?: number | undefined;
  integration_id: number;
  period: string;
  lag_minutes: number;
  integration?: Integration;
  include_location_references?: boolean;
  include_location_attributes?: boolean;
  include_site_references?: boolean;
  include_site_attributes?: boolean;
}

export interface ManagementIntegrationConfiguration {
  id?: number | undefined;
  type?: string;
  entity_id?: number;
  integration_id: number;
  integration?: Integration | DeviceIntegration;
  alerts_severity: number;
  alerts_suppress_repeats: boolean;
  alerts: boolean;
  alerts_cleared: boolean;
  tasks: boolean;
  project_updates: boolean;
  project_id?: number|null;
  alerts_count_threshold: number;
}

export enum IntegrationOutputType {
  STREAMING = 'streaming',
  PUBLISHING = 'publishing',
}

export interface Integration {
  id?: number | undefined;
  name: string;
  type: string;
  organisation_id?: number;
  channel_details: MQTTChannelDetails
                  | WebsocketChannelDetails
                  | SlackChannelDetails
                  | EmailChannelDetails
                  | OpsGenieChannelDetails
                  | S3BucketChannelDetails
                  | MySQLChannelDetails
                  | AWSIoTChannelDetails
                  | WebhookChannelDetails
                  | QSS3ChannelDetails;
  configurations: IntegrationConfiguration[];
  output_type?: IntegrationOutputType;
  enabled: boolean;
  payload_version: number;
}

export interface DeviceIntegration {
  id?: number | undefined;
  name: string;
  type: string;
  organisationId?: number;
}

export interface TestResult {
  success: boolean;
  message: string;
}

export interface SigfoxApiCredentials {
  id?: number | undefined;
  login: string;
  sigfox_group: string;
  default_contract_id: string;
}

export interface DataSet {
  id?: number | undefined;
  organisation_slug?: string;
  name: string;
  filters: string;
  streaming: boolean;
  publishing: boolean;
  query: boolean;
  owner: string;
  uuid?: string;
}

export interface DataSetMetadata {
  dataset_uuid: string;
  tags: Record<string, Array<string | number | IDataTableSelectableFilterValue> | undefined>;
  deviceTypes: Record<string, any>;
  interfaces: Record<string, any>;
}

export interface DataSetRule {
  id?: number | undefined;
  dataset_id: number;
  dataset_name: string;
  integrations: Integration[];
  name: string;
  desc: string | null;
  device_version_slug: string;
  conditions: RuleConditions | null;
  message: string;
  severity: string;
  created_at?: Date | null | undefined;
  updated_at?: Date | null | undefined;
  deleted_at?: Date | null | undefined;
  message_type: string;
  uuid?: string | undefined;
}

export interface Interface {
  id?: number | undefined;
  name: string;
  slug: string;
  fields: InterfaceField[];
  type?: string | null;
  created_at?: Date | null | undefined;
  updated_at?: Date | null | undefined;
  deleted_at?: Date | null | undefined;
}

export interface InterfaceField {
  id?: number | undefined;
  field_name: string;
  type: string;
  interface_id: number | undefined;
  description: string;
  created_at?: Date | null | undefined;
  updated_at?: Date | null | undefined;
  deleted_at?: Date | null | undefined;
  unit_id?: number | null;
  unit?: Unit | null | undefined;
}

export interface Unit {
  id?: number;
  symbol: string;
  description: string;
}

export interface DeviceVersionInterface {
  id?: number | undefined;
  device_version_id: number;
  interface_id: number | undefined;
  function_id: number | null;
  interface: Interface;
  function: DeviceFunction;
}

export interface DeviceFunction {
  id?: number | undefined;
  function: string;
  type: string;
  execution_version: string;
}
  // [{networkToken: "", activatedAt: null, expiresAt: null, createdAt: "2021-02-22T12:46:16.000Z",…},…]

export interface SubscriptionPlanType {
  id: number;
  code: string;
  description: string;
}

export interface Subscription {
  activated_at: string| null;
  expires_at: string| null;
  created_at: string;
  deleted_at: string| null;
  id: string;
  network_token: string;
  subscription_order_id?: number;
  auto_renew: boolean;
}

export interface SigfoxSubscriptionPool {
  id: number;
  contract_id: string;
  created_at: string;
  deleted_at: string| null;
  group: string| null;
  in_use_Subscriptions: number;
  max_subscriptions: number;
  name: string;
  reserved_subscriptions: number;
}

export interface SubscriptionPlan {
  start_at: string;
  activate_by: string;
  activated_at: string|null;
  created_at: string| null;
  deleted_at: string| null;
  id: number;
  name: string;
  total_subscriptions: number;
  active_subscriptions?: number;
  subscriptions: Subscription[];
  term_length: number;
  type: 'full'|'trial';
  organisation: Organisation;
  plan_type: SubscriptionPlanType;
  sigfox_subscription_pool?: SigfoxSubscriptionPool;
  updated_at: string| null;
}

export interface SubscriptionPlanRequest {
  id?: number;
  name: string;
  start_at: string;
  activate_by: string;
  term_length: number;
  total_subscriptions: number;
  type: 'full'|'trial';
  organisation_id: number;
  plan_type_code: string;
  sigfox_subscription_pool_id?: number;
}

export interface SubscriptionPlanType {
  code: string;
  description: string;
  id: number;
  has_sims: boolean;
}

export interface SubscriptionCounts {
  totalSubscriptions: number;
  allocationSims: number;
  pairedSims: number;
  unusedSubscriptions: number;
}

export interface LocationV2 {
  full_location: string;
  materialised_path: string;
  depth: number;
  name: string;
  description: string;
  is_site: boolean;
  coords: number[] | Array<null>;
  type: string;
  coords_type: string;
  path: string;
  address: string;
  parent_id: number | undefined;
  parent: LocationV2;
  children: LocationV2[];
  positions: Position[];
  device_version: DeviceVersion;
  device: Device;
  projects: Project[];
  references: LocationReference[];
  attributes: LocationAttribute[];
  organisation_id: number;
  id: number | undefined;
  gatewayPositions: GatewayPosition[];
}

export interface LocationTree {
  children: LocationTree[] | undefined;
  positions: Position[];
  gatewayPositions: GatewayPosition[];
  hasChildren: boolean;
}

export interface LocationAttribute {
  key: string;
  value: string;
}

export interface LocationReference {
  type: string;
  reference: string;
}

export interface Job {
  id: string;
  type: string;
  status: string;
  data: JobData;
}

export interface JobData {
  rows: number;
  imported: number;
  skipped: number;
  errors: string[];
}

export interface SimCard {
  gateway_id: number;
  id?: number;
  iccid: string;
  device_id: number;
  subscription_plan_id: number;
  network_operator_id: number;
  license: License;
}
export interface SimProduct {
  id?: number;
  name: string;
  description?: string;
  network_operator_id: number;
  sim_form_factors: string[];
  type: string;
  product_code: string;
  activation_period: number;
}
export interface SubscriptionProduct {
  id?: number;
  product_code: string;
  name: string;
  description: string | null;
  network_operator_id: number;
  term_length: number;
  data_allowance: Decimal | null;
  message_allowance: number | null;
  requires_sim: boolean;
  bearers: string[];
  activation_period: number;
  product_scope: string;
}

export interface SubscriptionOrder {
  id: number;
  order_number: string;
  description?: string;
  organisation_id?: number;
  organisation?: Organisation;
  commercial_account_id?: number;
  commercial_account?: CommercialAccount;
  allocations?: Array<{ organisationId: number; organisationName: string; allocatedSubscriptions: number }>;
  isManyToMany?: boolean;
  subscription_product_id?: number;
  subscription_product?: SubscriptionProduct;
  total_sim_cards?: number;
  total_subscriptions: number;
  active_subscriptions: number;
  spot_price: Decimal;
  activate_by: string | Date;
  currency: string;
}
export interface AccountSubscriptionOrder {
  id: number;
  orderNumber: string;
  description?: string;
  organisations?: Organisation[];
  account?: CommercialAccount;
  subscriptionProduct?: SubscriptionProduct;
  totalSimCards?: number;
  totalSubscriptions: number;
  activeSubscriptions: number;
  activateBy: string | Date;
  currency: string;
}

export interface ConsolidatedSubscriptionOrder {
  id: number;
  orderNumber: string;
  description?: string;
  account?: CommercialAccount;
  subscriptionProduct?: SubscriptionProduct;
  totalSimCards?: number;
  totalSubscriptions: number;
  activeSubscriptions: number;
  activateBy: string | Date;
  currency: string;
  organisation?: Organisation;
  spotPrice?: Decimal;
  allocations?: Allocation[];
  accountOrganisations?: Organisation[];
  isManyToMany?: boolean;
  quoteId?: string;
}

export interface Allocation {
  organisationId: number;
  organisationName: string;
  allocatedSubscriptions: number;
  consumedSubscriptions?: number;
}

export interface AdminSubscriptionSummary {
  organisation: string;
  commercial_account: string;
  subscription_product: string;
  subscription_id: string;
  subscription_created_at: Date | string;
  subscription_activate_by: Date | string;
  subscription_activated_at?: Date | string;
  subscription_expires_at?: Date | string;
  device_id?: string;
  device_deleted_at?: Date | string;
  network_id?: string;
  device_type?: string;
  device_first_deployed_at?: Date | string;
  device_stage?: string;
  sim_iccid?: string;
  subscription_order: string;
}

export interface AdminSubscriptionsResponse {
  items: any;
  total: number;
}


export interface SubscriptionsResponse {
  items: any;
  total: number;
  counts: { [key: string]: number };
}

export type SUB_ALLOCATE_RESULT = 'ok'|'no_sub'|'no_sim'|'cancelled'|'resub_not_supported'|'is_sigfox'|'not_expired'|'no_expiration'|'no_allocations'|'allocation_limit_reached'|'resubscribe_ok';

export interface MarketingDetails {
  id?: number;
  device_version_id: number;
  marketing_text: string;
  purchase_link_text: string;
  purchase_link_url: string;
  rating: number;
  review: string;
}

export interface CommercialAccount {
  id: number;
  name: string;
  contact_name: string;
  contact_email: string;
  home_organisation_id: number;
  licensed_at: Date;
  license_expires: Date;
  legal_entity_name?: string;
  sage_account_id?: string;
  organisations?: any[];

  accountOrganisations?: Organisation[];
  allocations?: Allocation[];
}

export interface License {
  id: number;
  organisation_id: number;
  sim_card: SimCard;
  sim_card_id: number;
  subscription: Subscription;
  subscription_id: string;
  gateway_id: number;
}

export interface Assembly {
  id: number;
  parent_position_id?: number;
  parent_position?: Position;
  local_nodes: LocalNode[];
}

export interface LocalNode {
  id?: number;
  position_id?: number;
  assembly_id?: number;
  assembly?: Assembly;
  position?: Position;
  local_address?: string;
}

export interface Audit {
  id: number;
  category: string;
  sub_category: string;
  summary: string;
  detail?: string;
  entity_id?: number;
  entity_name?: string;
  created_at: Date;
  updated_at: Date;
  deleted_at?: Date;
  user_id?: number;
}

export interface SigfoxDeviceDiagnostic {
  details: any;
  messages: any[];
  contract: any;
}

export interface UploadImageUrlResponse {
  urlInfo: {
    url: string;
    fields: any;
  };
  fileName: string;
}

export interface ImportLocationsResponse {
  uploadData: {
    url: string;
    fields: any;
  };
  job_id: string;
}

export interface ModbusRegisterConfig {
  id: number;
  name: string;
  device_version_id: number;
  device_version: DeviceVersion;
  register_type: 'coil' | 'discrete-input' | 'holding-register' | 'input-register';
  register_address: number;
  payload_type: null | 'uint16' | 'int16' | 'uint32' | 'int32' | 'float32' | 'uint32le' | 'int32le' | 'float32le';
}

export interface IdResponse {
  id: number;
}

export interface SuccessResponse {
  success: boolean;
}

export interface DataServiceResult {
  count?: number;
  results: any[];
  fields?: string[];
}

export interface SimRangeResponse {
  sim_cards: SimCard[];
  start_iccid: string;
  end_iccid: string;
  sim_product: SimProduct;
  activate_by: Date;
  created_at: Date;
  total: number;
}

export interface SimRange {
  start_iccid: string;
  end_iccid: string;
}

export interface SimFormFactor {
  id: number;
  name: string;
  description?: string;
  short_code: string;
}

export interface NetworkOperator {
  id: number;
  name: string;
  description?: string;
  sims: boolean;
}

export interface ExplorerView {
  id?: number;
  name: string;
  organisation_id: number;
  owner_id: number;
  is_public: boolean;
  dataset_uuid: string;
  dataset_name: string;
  configuration: ExplorerConfiguration;
}

export interface ExplorerConfiguration {
  filters: Record<string, any>;
  layout: Record<string, IDataTableHeader[]>;
}

export interface LoRaNetworkServer {
  name: string;
  host: string;
  thirdParty: boolean;
  display?: boolean;
  default?: boolean;
  id?: number;
}

export interface PaginationOptions {
  itemsPerPage: number;
}

export interface DeviceShadow {
  device: { [k: string]: string | number | boolean };
  shadow: { [k: string]: string | number | boolean |  { [k: string]: string | number | boolean } };
}

export interface FrequencyPlanOption {
  id: string;
  name: string;
  region: string;
}

export interface Gateway {
  id: number;
  eui: string;
  name: string;
  position: GatewayPosition;
  gateway_type_id: number;
  organisation_id: number;
  location_id?: number;
  gateway_type: GatewayType;
  stage_id: number;
  project_id?: number;
  require_authenticated_selection: boolean;
  status: boolean;
  keep_alive?: number | null;
  alerts_enabled: boolean;
  stage: Stage;
  subscription: Subscription;
  subscription_product?: SubscriptionProduct;
  license: License;
  iccid: string;
  other_connection_type: boolean;
  application_server?: string;
  frequency_plan_id: string | null;
  lns_id: number | null;
  lns_name: string | null;
}

export interface GatewayType {
  id: number;
  name: string;
  slug: string;
  image: string;
  has_cellular_backhaul: boolean;
}

export interface GatewayPosition {
  id: number;
  gateway: Gateway;
  gateway_type: GatewayType;
  gateway_type_id: number;
  location: LocationV2;
  location_id: number;
  project_id: number;
}

export interface NotificationBanner {
  id?: number;
  start_date: Date;
  end_date: Date;
  message: string;
  severity: 'info' | 'warning' | 'error';
}

export interface NbiotSubscriptionEvent {
  occurred_at: string;
  apn: string;
  imei: string;
  rat_type: string;
  terminal_ip: string;
  region: string;
  operator: string;
}

export interface NbiotDataUsage {
  uploaded: number;
  downloaded: number;
}

export interface NbiotNetworkData {
  subscriptionEvents: NbiotSubscriptionEvent[];
  dataUsage: NbiotDataUsage[];
}
